import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useToast } from '../contexts/ToastContext';
import { SystemHealth, Analytics, Alert as AlertType, AlertsResponse, StandardAPIResponse } from '../types/monitoring';
import { extractApiData, isApiResponseSuccess } from '../utils/apiResponseHandler';

export const AdminMonitoringPage: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [alerts, setAlerts] = useState<AlertType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'health' | 'analytics' | 'alerts'>('health');

  const loadSystemHealth = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.request<StandardAPIResponse<SystemHealth>>('/monitoring/health/');
      const healthData = extractApiData<SystemHealth>(response);
      setSystemHealth(healthData);
    } catch (error) {
      console.error('Failed to load system health:', error);
      showToast('Failed to load system health', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.request<StandardAPIResponse<Analytics>>('/monitoring/analytics/admin/');
      const analyticsData = extractApiData<Analytics>(response);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      showToast('Failed to load analytics', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAlerts = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.request<StandardAPIResponse<AlertsResponse>>('/monitoring/alerts/');
      const alertsData = extractApiData<AlertsResponse>(response);
      setAlerts(alertsData?.alerts || []);
    } catch (error) {
      console.error('Failed to load alerts:', error);
      showToast('Failed to load alerts', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const testAlert = async () => {
    try {
      const response = await apiService.request<StandardAPIResponse>('/monitoring/test-alert/', {
        method: 'POST',
        body: JSON.stringify({
          type: 'test',
          message: 'Test alert from admin dashboard'
        })
      });
      
      // Check if response indicates success
      if (isApiResponseSuccess(response)) {
        showToast('Test alert sent successfully', 'success');
        loadAlerts();
      } else {
        showToast('Test alert sent (no confirmation)', 'success');
        loadAlerts();
      }
    } catch (error) {
      console.error('Failed to send test alert:', error);
      showToast('Failed to send test alert', 'error');
    }
  };

  useEffect(() => {
    loadSystemHealth();
    loadAnalytics();
    loadAlerts();
  }, []);

  const getHealthColor = (score: number) => {
    if (score > 0.8) return 'text-green-600 dark:text-green-400';
    if (score > 0.5) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getHealthIcon = (score: number) => {
    if (score > 0.8) return '✅';
    if (score > 0.5) return '⚠️';
    return '❌';
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      case 'warning': return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
      case 'info': return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
      default: return 'text-gray-600 bg-gray-50 border-gray-200 dark:border-gray-700';
    }
  };

  if (!user?.isStaff && !user?.isSuperuser) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-300">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">System Monitoring</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">Monitor system health, analytics, and alerts</p>
            </div>

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-gray-200 dark:border-gray-700 overflow-x-auto custom-scrollbar">
          <nav className="flex space-x-6 sm:space-x-8 min-w-max pb-px">
            {[
              { id: 'health', label: 'System Health' },
              { id: 'analytics', label: 'Analytics' },
              { id: 'alerts', label: 'Alerts' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* System Health Tab */}
        {activeTab === 'health' && (
          <div className="space-y-6">
            {systemHealth && (
              <>
                {/* Overall Health */}
                <Card className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Overall System Health</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                        Last updated: {new Date(systemHealth.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <div className="sm:text-right">
                      <div className={`text-3xl font-bold ${getHealthColor(systemHealth.health_score)}`}>
                        {getHealthIcon(systemHealth.health_score)} {Math.round(systemHealth.health_score * 100)}%
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300 capitalize">
                        {systemHealth.overall_health}
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Component Health */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="p-6">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Database</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Status</span>
                        <span className={`text-sm font-medium ${getHealthColor(systemHealth.database.score)}`}>
                          {getHealthIcon(systemHealth.database.score)} {systemHealth.database.status}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Response Time</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {systemHealth.database.response_time?.toFixed(3)}s
                        </span>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Cache</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Status</span>
                        <span className={`text-sm font-medium ${getHealthColor(systemHealth.cache.score)}`}>
                          {getHealthIcon(systemHealth.cache.score)} {systemHealth.cache.status}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Response Time</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {systemHealth.cache.response_time?.toFixed(3)}s
                        </span>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">APIs</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Status</span>
                        <span className={`text-sm font-medium ${getHealthColor(systemHealth.apis.score)}`}>
                          {getHealthIcon(systemHealth.apis.score)} {systemHealth.apis.status}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Active APIs</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {systemHealth.apis.active_apis}/{systemHealth.apis.total_apis}
                        </span>
                      </div>
                    </div>
                  </Card>
                </div>
              </>
            )}

            <div className="flex justify-end">
              <Button onClick={loadSystemHealth} disabled={isLoading}>
                {isLoading ? 'Refreshing...' : 'Refresh Health'}
              </Button>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {analytics && (
              <>
                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <Card className="p-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-2">Total Searches</h4>
                    <div className="text-3xl font-bold text-blue-600">{analytics.total_searches}</div>
                    <div className="text-sm text-gray-600">Last {analytics.period_days} days</div>
                  </Card>

                  <Card className="p-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-2">Success Rate</h4>
                    <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {analytics.search_success_rate.toFixed(1)}%
                  </div>
                    <div className="text-sm text-gray-600">Search success rate</div>
                  </Card>

                  <Card className="p-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-2">API Calls</h4>
                    <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">{analytics.total_api_calls}</div>
                    <div className="text-sm text-gray-600">Total API requests</div>
                  </Card>

                  <Card className="p-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-2">Active Users</h4>
                    <div className="text-3xl font-bold text-orange-600">{analytics.active_users}</div>
                    <div className="text-sm text-gray-600">Unique users</div>
                  </Card>
                </div>

                {/* Popular Parameter Types */}
                <Card className="p-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Popular Parameter Types</h4>
                  <div className="space-y-3">
                    {analytics.popular_parameter_types.map((type, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">{type.parameter_type__display_name}</span>
                        <span className="text-sm font-medium text-gray-900">{type.count} searches</span>
                </div>
                    ))}
                  </div>
                </Card>
              </>
            )}

            <div className="flex justify-end">
              <Button onClick={loadAnalytics} disabled={isLoading}>
                {isLoading ? 'Refreshing...' : 'Refresh Analytics'}
              </Button>
            </div>
          </div>
        )}

        {/* Alerts Tab */}
        {activeTab === 'alerts' && (
                <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">System Alerts</h3>
              <Button onClick={testAlert} className="bg-yellow-600 hover:bg-yellow-700">
                Send Test Alert
              </Button>
                    </div>

            {alerts.length === 0 ? (
              <Card className="p-6 text-center">
                <div className="text-gray-500">
                  <div className="text-4xl mb-2">✅</div>
                  <p>No active alerts</p>
                </div>
              </Card>
            ) : (
                <div className="space-y-4">
                {alerts.map((alert, index) => (
                  <Card key={index} className={`p-4 border-l-4 ${getSeverityColor(alert.severity)}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-gray-900">{alert.message}</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Type: {alert.type} • {new Date(alert.timestamp).toLocaleString()}
                        </p>
                        {alert.api_name && (
                          <p className="text-sm text-gray-500 mt-1">API: {alert.api_name}</p>
                        )}
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        alert.severity === 'critical' ? 'bg-red-100 dark:bg-red-900/30 text-red-800' :
                        alert.severity === 'warning' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800' :
                        'bg-blue-100 dark:bg-blue-900/30 text-blue-800'
                      }`}>
                        {alert.severity}
                      </span>
                    </div>
                  </Card>
                  ))}
              </div>
            )}

            <div className="flex justify-end">
              <Button onClick={loadAlerts} disabled={isLoading}>
                {isLoading ? 'Refreshing...' : 'Refresh Alerts'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 